import { useEffect } from 'react';
import { z } from 'zod';
import type { Row } from '../api/types';
import type { AnyForm } from '../forms/fields';
import { amountReq, dateTimeReq, req, selectOpt, selectReq, toId, toIdOrNull, toText } from '../forms/zod';
import { BalanceCard } from '../ui/BalanceCard';
import type { Column } from '../ui/DataTable';
import { Amount, fmtDateTime, fromDateTimeInput, nowInput, toDateTimeInput } from '../ui/format';
import { toOptions, useLookup } from '../ui/lookups';
import type { CrudConfig, FieldDef, FilterDef } from './CrudScreen';

export const TROUGH_BANK = '2';
export const TROUGH_CASH = '1';
export const CREDIT = '1';
export const DEBIT = '2';

// ---------------------------------------------------------------- shared pieces
export const typeBadge = (r: Row) =>
  r.type ? <span className={`badge text-bg-${r.type === 'Credit' ? 'success' : 'warning'}`}>{r.type}</span> : null;

// On a phone (< 768px) only Date, the party, Amount and Type survive in the list; everything else (No,
// Currency, Via, Bank, Details) is one tap away in the edit/view action. Applied here once so every
// transaction table gets the same, consistent narrow view.
export const dateCol: Column = { key: 'transactionDate', header: 'Date', sortKey: 'date', className: 'text-nowrap', render: (r) => fmtDateTime(r.transactionDate) };
export const noCol: Column = { key: 'transactionNo', header: 'No', sortKey: 'no', className: 'text-nowrap text-body-secondary', hideOnMobile: true };
const currencyCol: Column = { key: 'currencyCode', header: 'Cur', hideOnMobile: true };
const amountCol = (header = 'Amount', key = 'amount', hideOnMobile = false): Column => ({ key, header, sortKey: key === 'amount' ? 'amount' : undefined, align: 'end', hideOnMobile, render: (r) => <Amount value={r[key]} /> });
const troughCol: Column = { key: 'trough', header: 'Via', hideOnMobile: true };
const typeCol: Column = { key: 'type', header: 'Type', render: typeBadge };
const bankCol: Column = { key: 'bankName', header: 'Bank', hideOnMobile: true };
const detailsCol = (key = 'details'): Column => ({ key, header: 'Details', className: 'text-truncate', hideOnMobile: true, render: (r) => <span title={r[key] ?? ''} className="d-inline-block text-truncate" style={{ maxWidth: 220 }}>{r[key] ?? ''}</span> });

/** Cash/Bank/Credit/Debit lookups and the bank list for a currency, plus "clear bank when it no longer applies". */
export function useBankPicker(form: AnyForm, currencyId: number | undefined) {
  const troughs = useLookup('transaction-troughs');
  const types = useLookup('transaction-types');
  const banks = useLookup('banks');
  const trough = form.watch('troughId') as string;
  const bankId = form.watch('bankId') as string;
  const isBank = trough === TROUGH_BANK;
  const bankOptions = toOptions(banks.data?.filter((b) => currencyId !== undefined && b.currencyId === currencyId));

  useEffect(() => {
    if (!isBank && bankId) form.setValue('bankId', '');
  }, [isBank, bankId, form]);
  useEffect(() => {
    if (isBank && bankId && banks.data && !bankOptions.some((o) => o.value === bankId)) form.setValue('bankId', '');
  }, [isBank, bankId, bankOptions.map((o) => o.value).join(','), banks.data, form]); // eslint-disable-line react-hooks/exhaustive-deps

  const fields = (): FieldDef[] => [
    { name: 'troughId', label: 'Paid through', type: 'select', required: true, options: toOptions(troughs.data), placeholder: 'Select…' },
    { name: 'bankId', label: 'Bank', type: 'select', required: isBank, disabled: !isBank, options: bankOptions, placeholder: isBank ? (currencyId ? 'Select a bank…' : 'Choose the party first') : 'Not needed for cash' },
    { name: 'typeId', label: 'Type', type: 'select', required: true, options: toOptions(types.data), placeholder: 'Select…' },
  ];
  return { fields, isBank };
}

export const trioSchema = {
  troughId: selectReq('Select cash or bank'),
  bankId: selectOpt,
  typeId: selectReq('Select credit or debit'),
  transactionDate: dateTimeReq,
};

/** Bank is required exactly when the trough is Bank (mirrors the API rule). */
export const bankRule = (schema: z.ZodObject<z.ZodRawShape>) =>
  schema.superRefine((v, ctx) => {
    const values = v as Record<string, unknown>;
    if (values.troughId === TROUGH_BANK && !values.bankId) ctx.addIssue({ code: 'custom', path: ['bankId'], message: 'Select a bank' });
  });

const common = (v: Record<string, any>) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
  amount: v.amount.trim(),
  transactionDate: fromDateTimeInput(v.transactionDate),
});

export const trioPayload = (v: Record<string, any>) => ({ // eslint-disable-line @typescript-eslint/no-explicit-any
  troughId: toId(v.troughId), bankId: toIdOrNull(v.bankId), typeId: toId(v.typeId),
});

export const trioForm = (r: Row) => ({
  amount: r.amount, troughId: String(r.troughId), bankId: r.bankId ? String(r.bankId) : '', typeId: String(r.typeId),
  transactionDate: toDateTimeInput(r.transactionDate),
});

export const trioDefaults = (type: string) => ({ amount: '', troughId: TROUGH_CASH, bankId: '', typeId: type, transactionDate: nowInput() });

export function useTroughTypeFilters(): FilterDef[] {
  const troughs = useLookup('transaction-troughs');
  const types = useLookup('transaction-types');
  return [
    { param: 'troughId', label: 'Via', options: toOptions(troughs.data) },
    { param: 'typeId', label: 'Type', options: toOptions(types.data) },
  ];
}

export const base = { update: 'put' as const, dateRange: true, initialSort: { key: 'date', dir: 'desc' as const }, modalSize: 'lg' as const };
